import * as d3 from 'd3'

import type { BidLevel, ChartScales } from '@/features/bidding/types/bidLadder'

type SupplyPoint = { x: number; y: number }
type MarketDirection = 'up' | 'down'

type BidStepData = {
  bid: BidLevel
  index: number
  prevVolume: number
  currentVolume: number
  x1: number
  x2: number
  currentPrice: number
  y: number
}

function buildSupplyPoints(
  bids: BidLevel[],
  direction: number,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
): SupplyPoint[] {
  const points: SupplyPoint[] = [{ x: xScale(0), y: yScale(0) }]

  for (const bid of bids) {
    const volume = direction * bid.cumulativeVolumeMW
    const currentPrice = bid.pricing.type === 'fixed' ? bid.pricing.fixedPrice : bid.pricing.avgPrice

    points.push({ x: xScale(volume), y: yScale(currentPrice) })
  }

  return points
}

function renderSupplyCurve(
  marketGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  supplyPoints: SupplyPoint[],
  chartHeight: number,
) {
  const line = d3
    .line<SupplyPoint>()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(d3.curveStepBefore)

  const area = d3
    .area<SupplyPoint>()
    .x((d) => d.x)
    .y0(chartHeight)
    .y1((d) => d.y)
    .curve(d3.curveStepBefore)

  // Draw shaded area under the curve
  marketGroup.append('path').datum(supplyPoints).attr('d', area).attr('fill', 'transparent')

  // Draw the line on top
  marketGroup
    .append('path')
    .datum(supplyPoints)
    .attr('d', line)
    .attr('fill', 'none')
    .attr('stroke', '#111827')
    .attr('stroke-width', 1)
    .attr('stroke-linecap', 'round')
    .attr('stroke-linejoin', 'round')
}

function renderRangeIndicator(
  rangeGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  xCenter: number,
  yMin: number,
  yMax: number,
  yAvg: number,
) {
  const capWidth = 10

  // Vertical line from minPrice to maxPrice
  rangeGroup
    .append('line')
    .attr('x1', xCenter)
    .attr('y1', yMax)
    .attr('x2', xCenter)
    .attr('y2', yMin)
    .attr('stroke', '#374151')
    .attr('stroke-width', 1)

  // Upper horizontal cap at maxPrice
  rangeGroup
    .append('line')
    .attr('x1', xCenter - capWidth / 2)
    .attr('y1', yMax)
    .attr('x2', xCenter + capWidth / 2)
    .attr('y2', yMax)
    .attr('stroke', '#374151')
    .attr('stroke-width', 1)
    .attr('stroke-linecap', 'round')

  // Lower horizontal cap at minPrice
  rangeGroup
    .append('line')
    .attr('x1', xCenter - capWidth / 2)
    .attr('y1', yMin)
    .attr('x2', xCenter + capWidth / 2)
    .attr('y2', yMin)
    .attr('stroke', '#374151')
    .attr('stroke-width', 1)
    .attr('stroke-linecap', 'round')

  // Add small midpoint marker at avgPrice
  rangeGroup.append('circle').attr('cx', xCenter).attr('cy', yAvg).attr('r', 1.5).attr('fill', '#374151')
}

function renderRangeIndicators(
  marketGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  bidSteps: BidStepData[],
  yScale: d3.ScaleLinear<number, number>,
) {
  for (const step of bidSteps) {
    if (step.bid.pricing.type !== 'range') continue

    const xCenter = (step.x1 + step.x2) / 2
    const yAvg = yScale(step.bid.pricing.avgPrice)
    const yMin = yScale(step.bid.pricing.minPrice)
    const yMax = yScale(step.bid.pricing.maxPrice)

    const rangeGroup = marketGroup.append('g').attr('class', 'range-indicator')
    renderRangeIndicator(rangeGroup, xCenter, yMin, yMax, yAvg)
  }
}

function addLabelWithWidthCheck(
  labelGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  text: string,
  x: number,
  y: number,
  anchor: string,
  stepWidth: number,
  fontSize: string = '9px',
  fontWeight: number = 600,
  fill: string = '#111827',
) {
  const tempText = labelGroup
    .append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', anchor)
    .attr('font-size', fontSize)
    .attr('font-weight', fontWeight)
    .text(text)

  const textWidth = (tempText.node() as SVGTextElement).getBBox().width

  if (textWidth > stepWidth) {
    tempText.remove()
    return
  }

  tempText.attr('fill', fill).attr('pointer-events', 'none')
}

function renderFixedPriceLabels(
  labelGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  bid: BidLevel,
  labelX: number,
  y: number,
  textAnchor: string,
  stepWidth: number,
) {
  if (bid.pricing.type !== 'fixed') return

  const labelY = y - 2
  addLabelWithWidthCheck(labelGroup, `€${bid.pricing.fixedPrice.toFixed(0)}`, labelX, labelY, textAnchor, stepWidth)

  addLabelWithWidthCheck(
    labelGroup,
    `${bid.volumeMW.toFixed(0)} MW`,
    labelX,
    y + 10,
    textAnchor,
    stepWidth,
    '9px',
    500,
    '#6b7280',
  )
}

function renderRangePriceLabels(
  labelGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  bid: BidLevel,
  labelX: number,
  textAnchor: string,
  stepWidth: number,
  yScale: d3.ScaleLinear<number, number>,
) {
  if (bid.pricing.type !== 'range') return

  const yMax = yScale(bid.pricing.maxPrice)
  const yMin = yScale(bid.pricing.minPrice)

  addLabelWithWidthCheck(labelGroup, `€${bid.pricing.maxPrice.toFixed(0)}`, labelX, yMax - 4, textAnchor, stepWidth)

  addLabelWithWidthCheck(labelGroup, `€${bid.pricing.minPrice.toFixed(0)}`, labelX, yMin + 12, textAnchor, stepWidth)

  addLabelWithWidthCheck(
    labelGroup,
    `${bid.volumeMW.toFixed(0)} MW`,
    labelX,
    yMin + 24,
    textAnchor,
    stepWidth,
    '9px',
    500,
    '#6b7280',
  )
}

function renderBidLabels(
  marketGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  bidSteps: BidStepData[],
  market: MarketDirection,
  yScale: d3.ScaleLinear<number, number>,
) {
  const minWidthForLabels = 25
  const textAnchor = market === 'up' ? 'start' : 'end'

  for (const step of bidSteps) {
    const stepWidth = Math.abs(step.x2 - step.x1)
    if (stepWidth < minWidthForLabels) continue

    const labelX = market === 'up' ? step.x1 + 4 : step.x1 - 4
    const labelGroup = marketGroup.append('g').attr('class', 'bid-labels')

    if (step.bid.pricing.type === 'fixed') {
      renderFixedPriceLabels(labelGroup, step.bid, labelX, step.y, textAnchor, stepWidth)
    } else {
      renderRangePriceLabels(labelGroup, step.bid, labelX, textAnchor, stepWidth, yScale)
    }
  }
}

function renderHoverInteractions(
  marketGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  bidSteps: BidStepData[],
  chartHeight: number,
) {
  for (const step of bidSteps) {
    const hoverGroup = marketGroup.append('g').attr('class', 'hover-area')

    hoverGroup
      .append('rect')
      .attr('x', Math.min(step.x1, step.x2))
      .attr('y', step.y)
      .attr('width', Math.abs(step.x2 - step.x1))
      .attr('height', chartHeight - step.y)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mouseenter', function () {
        hoverGroup
          .append('rect')
          .attr('class', 'highlight-rect')
          .attr('x', Math.min(step.x1, step.x2))
          .attr('y', step.y)
          .attr('width', Math.abs(step.x2 - step.x1))
          .attr('height', chartHeight - step.y)
          .attr('fill', '#4B5563')
          .attr('opacity', 0.1)
          .attr('pointer-events', 'none')
      })
      .on('mouseleave', function () {
        hoverGroup.selectAll('.highlight-rect').remove()
      })
  }
}

function prepareBidSteps(
  bids: BidLevel[],
  direction: number,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
): BidStepData[] {
  return bids.map((bid, index) => {
    const prevCumulative = index > 0 ? bids[index - 1].cumulativeVolumeMW : 0
    const currentVolume = direction * bid.cumulativeVolumeMW
    const prevVolume = direction * prevCumulative
    const currentPrice = bid.pricing.type === 'fixed' ? bid.pricing.fixedPrice : bid.pricing.avgPrice

    return {
      bid,
      index,
      prevVolume,
      currentVolume,
      x1: xScale(prevVolume),
      x2: xScale(currentVolume),
      currentPrice,
      y: yScale(currentPrice),
    }
  })
}

export function renderButterflyBidLadder(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  bids: BidLevel[],
  market: MarketDirection,
  scales: ChartScales,
) {
  const { xScale, yScale, chartHeight } = scales
  const marketGroup = g.append('g').attr('class', `bid-ladder-${market}`)
  const direction = market === 'up' ? 1 : -1

  // Build supply curve and render it
  const supplyPoints = buildSupplyPoints(bids, direction, xScale, yScale)
  renderSupplyCurve(marketGroup, supplyPoints, chartHeight)

  // Prepare bid step data once to avoid repeated calculations
  const bidSteps = prepareBidSteps(bids, direction, xScale, yScale)

  // Render all visual elements
  renderRangeIndicators(marketGroup, bidSteps, yScale)
  renderBidLabels(marketGroup, bidSteps, market, yScale)
  renderHoverInteractions(marketGroup, bidSteps, chartHeight)
}
